/**
 * Lazy-loaded Supabase module accessor.
 *
 * Why this exists (D8 / Lighthouse Win 3):
 *
 * `lib/supabase.ts` calls `createSupabaseClient(...)` at module load and
 * sets up a realtime subscription. The Supabase SDK is ~32 KiB gzipped.
 * Because `authStore.ts` and `AuthStateHandler.tsx` both `import` from
 * `lib/supabase.ts` at the module top level, Vite includes the SDK in
 * the eager chunk of every page that uses authStore — including the
 * marketing homepage where the SDK isn't needed until the user clicks
 * "Sign in".
 *
 * Lighthouse mobile flagged 32 KiB of Supabase as unused JS at boot.
 *
 * This module exposes a dynamic-import wrapper so callers in the
 * critical path of the public homepage can defer the SDK download
 * until the first auth action actually fires. The result is cached so
 * subsequent calls are synchronous after the first await.
 *
 * Trade-off: the very first auth action (Sign in / Sign up / load
 * existing session) now incurs the SDK download + parse — ~100-200 ms
 * on fast 4G. After that, everything is cached.
 *
 * Callers that aren't on the homepage critical path (modals, admin
 * pages, /pro pages, etc.) can keep importing `lib/supabase`
 * directly — Vite tree-shakes their chunks separately.
 */
import type * as SupabaseModule from './supabase';

let _modulePromise: Promise<typeof SupabaseModule> | null = null;

/** Returns the cached `lib/supabase` module, dynamically loading it on
 *  first call. Idempotent — concurrent callers share the same promise. */
export function getSupabaseModule(): Promise<typeof SupabaseModule> {
  if (!_modulePromise) {
    _modulePromise = import('./supabase');
  }
  return _modulePromise;
}

/** Convenience for callers that only need the supabase client. */
export async function getSupabase(): Promise<SupabaseModule['supabase']> {
  const m = await getSupabaseModule();
  return m.supabase;
}
